import React, { createContext, useContext, useState, useCallback } from 'react';

const LanguageContext = createContext();

export const SUPPORTED_LANGUAGES = [
  { code: 'fr', label: 'Français', short: 'FR', flag: '🇫🇷' },
  { code: 'en', label: 'English', short: 'EN', flag: '🇬🇧' },
  { code: 'es', label: 'Español', short: 'ES', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', short: 'DE', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', short: 'IT', flag: '🇮🇹' },
  { code: 'pt', label: 'Português', short: 'PT', flag: '🇵🇹' },
];

const STORAGE_KEY = 'meteris_lang';
const VALID_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

function _readStoredLang() {
  if (typeof window === 'undefined') return 'fr';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_CODES.includes(stored)) return stored;
  } catch (_) {}
  return 'fr';
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(_readStoredLang);
  const setLang = useCallback((newLang) => {
    if (!VALID_CODES.includes(newLang)) return;
    setLangState(newLang);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(STORAGE_KEY, newLang); } catch (_) {}
    }
  }, []);
  // Conserve pour compat : cycle fr -> en (legacy). Nouveau code doit utiliser setLang directement.
  const toggleLang = useCallback(() => {
    setLang(lang === 'fr' ? 'en' : 'fr');
  }, [lang, setLang]);
  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLang must be used within LanguageProvider');
  return context;
}

// Fallback chain : lang demandée -> en -> fr -> '' (chrome non-traduit reste en EN ou FR).
export function t(obj, lang) {
  if (!obj) return '';
  return obj[lang] || obj.en || obj.fr || '';
}

// Helper pour les strings inline traduites (ex: `lang === 'fr' ? 'X' : 'Y'`).
// Permet de garder un appel court tout en supportant les 6 langues sans toucher
// translations.js. Utilisation : tx(lang, 'FR', 'EN', { es: '...', de: '...', it: '...', pt: '...' }).
// Le 4e argument est optionnel — sans lui, fallback EN pour es/de/it/pt.
export function tx(lang, fr, en, more) {
  if (lang === 'fr') return fr;
  if (lang === 'en' || !more) return en;
  return more[lang] || en;
}

// Locale BCP-47 pour Intl/Date.toLocaleString d'après la lang active.
const LOCALES = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT' };
export function getLocale(lang) {
  return LOCALES[lang] || 'fr-FR';
}

// Helper i18n pour les champs dynamiques venant du backend.
// Gère 2 schémas distincts :
//   A) Multi-suffixes (col_news) : title_fr/title_en/title_es/title_de/title_it/title_pt
//   B) FR-default + suffixes (col_it_booking_types, col_it_configurator_steps) :
//      label (FR de base) + label_en + label_es + label_de + label_it + label_pt
// Cascade :
//   1. obj[key_<lang>] exact (couvre tous les cas où la trad existe)
//   2. Si lang === 'fr' : fallback obj[key] (schema B), sinon EN (dernier recours)
//   3. Sinon (lang étrangère) : fallback obj[key_en] -> obj[key] -> obj[key_fr] -> ''
//      (préférer EN à FR pour un utilisateur étranger)
export function i18nField(obj, key, lang) {
  if (!obj) return '';
  const exact = obj[`${key}_${lang}`];
  if (exact) return exact;
  if (lang === 'fr') {
    return obj[key] || obj[`${key}_en`] || '';
  }
  return obj[`${key}_en`] || obj[key] || obj[`${key}_fr`] || '';
}
